import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { format } from "date-fns";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  ArrowLeft, Loader2, ChevronDown, ChevronRight, ArrowRight,
  TrendingUp, TrendingDown, Minus, AlertTriangle, Camera
} from "lucide-react";
import { API } from "@/App";

const CONDITION_COLORS = {
  excellent: "bg-green-500",
  good: "bg-emerald-500",
  fair: "bg-amber-500",
  poor: "bg-orange-500",
  very_poor: "bg-red-500",
  new: "bg-blue-500"
};

const CHANGE_STATUS_CONFIG = {
  same: { icon: Minus, color: "text-slate-400", bg: "bg-slate-100", label: "No Change" },
  improved: { icon: TrendingUp, color: "text-green-600", bg: "bg-green-100", label: "Improved" },
  deteriorated: { icon: TrendingDown, color: "text-red-600", bg: "bg-red-100", label: "Deteriorated" },
  new_item: { icon: AlertTriangle, color: "text-amber-600", bg: "bg-amber-100", label: "New Item" }
};

export default function ReportComparisonPage() {
  const { reportId } = useParams();
  const navigate = useNavigate();
  
  const [comparison, setComparison] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedRooms, setExpandedRooms] = useState([]);
  const [showPhotos, setShowPhotos] = useState(null);

  const token = localStorage.getItem("token");
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchComparison();
  }, [reportId]);

  const fetchComparison = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/inventory-reports/${reportId}/comparison`, { headers });
      setComparison(response.data);
      // Auto-expand all rooms
      setExpandedRooms(response.data.rooms?.map(r => r.room_id) || []);
    } catch (err) {
      console.error("Error fetching comparison:", err);
      toast.error(err.response?.data?.detail || "Failed to load comparison");
    } finally {
      setLoading(false);
    }
  };

  const toggleRoom = (roomId) => {
    setExpandedRooms(prev => 
      prev.includes(roomId) 
        ? prev.filter(id => id !== roomId)
        : [...prev, roomId]
    );
  };

  // Count changes
  const getChangeSummary = () => {
    if (!comparison?.rooms) return { same: 0, improved: 0, deteriorated: 0, new_item: 0 };
    
    const summary = { same: 0, improved: 0, deteriorated: 0, new_item: 0 };
    comparison.rooms.forEach(room => {
      room.items?.forEach(item => {
        summary[item.change_status] = (summary[item.change_status] || 0) + 1;
      });
    });
    return summary;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  if (!comparison) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Card className="max-w-md">
          <CardContent className="pt-6 text-center">
            <AlertTriangle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold mb-2">No Comparison Available</h2>
            <p className="text-muted-foreground mb-4">
              This report doesn't have a linked check-in report for comparison.
            </p>
            <Button onClick={() => navigate(-1)}>Go Back</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const changeSummary = getChangeSummary();
  const checkoutReport = comparison.checkout_report;
  const checkinReport = comparison.checkin_report;

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b sticky top-14 z-10">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="text-xl font-bold">Before & After Comparison</h1>
                <p className="text-muted-foreground">{checkoutReport?.property_address}</p>
              </div>
            </div>
            <Badge variant="outline" className="text-lg px-4 py-1">
              Check-Out Report
            </Badge>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-6">
        {/* Report Info Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <Card className="bg-blue-50 border-blue-200">
            <CardContent className="pt-4">
              <div className="flex items-center gap-2 mb-2">
                <Badge className="bg-blue-600">Check-In (Before)</Badge>
              </div>
              <p className="font-medium">{checkinReport?.report_type_label}</p>
              <p className="text-sm text-muted-foreground">
                {checkinReport?.visit_date ? format(new Date(checkinReport.visit_date), "dd MMM yyyy") : "N/A"}
              </p>
              <p className="text-sm text-muted-foreground">
                Tenant: {checkinReport?.tenant_name || "N/A"}
              </p>
            </CardContent>
          </Card>

          <Card className="bg-purple-50 border-purple-200">
            <CardContent className="pt-4">
              <div className="flex items-center gap-2 mb-2">
                <Badge className="bg-purple-600">Check-Out (After)</Badge>
              </div>
              <p className="font-medium">{checkoutReport?.report_type_label}</p>
              <p className="text-sm text-muted-foreground">
                {checkoutReport?.visit_date ? format(new Date(checkoutReport.visit_date), "dd MMM yyyy") : "N/A"}
              </p>
              <p className="text-sm text-muted-foreground">
                Tenant: {checkoutReport?.tenant_name || "N/A"}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Change Summary */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Change Summary</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {Object.entries(CHANGE_STATUS_CONFIG).map(([key, config]) => {
                const Icon = config.icon;
                return (
                  <div key={key} className={`${config.bg} rounded-lg p-4 text-center`}>
                    <Icon className={`w-8 h-8 mx-auto ${config.color} mb-2`} />
                    <p className="text-2xl font-bold">{changeSummary[key] || 0}</p>
                    <p className="text-sm text-muted-foreground">{config.label}</p>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Room by Room Comparison */}
        <div className="space-y-4">
          <h2 className="text-lg font-semibold">Room-by-Room Comparison</h2>
          
          {comparison.rooms?.map((room) => (
            <Card key={room.room_id} className="overflow-hidden">
              <div 
                className="p-4 flex items-center justify-between cursor-pointer hover:bg-slate-50"
                onClick={() => toggleRoom(room.room_id)}
              >
                <div className="flex items-center gap-3">
                  {expandedRooms.includes(room.room_id) ? (
                    <ChevronDown className="w-5 h-5 text-slate-400" />
                  ) : (
                    <ChevronRight className="w-5 h-5 text-slate-400" />
                  )}
                  <div>
                    <h3 className="font-semibold">{room.room_name}</h3>
                    <p className="text-sm text-muted-foreground">
                      {room.items?.length || 0} items compared
                    </p>
                  </div>
                </div>
                
                {/* Room change indicators */}
                <div className="flex items-center gap-2">
                  {room.items?.filter(i => i.change_status === 'deteriorated').length > 0 && (
                    <Badge variant="destructive" className="gap-1">
                      <TrendingDown className="w-3 h-3" />
                      {room.items.filter(i => i.change_status === 'deteriorated').length}
                    </Badge>
                  )}
                  {room.items?.filter(i => i.change_status === 'improved').length > 0 && (
                    <Badge className="bg-green-600 gap-1">
                      <TrendingUp className="w-3 h-3" />
                      {room.items.filter(i => i.change_status === 'improved').length}
                    </Badge>
                  )}
                </div>
              </div>

              {expandedRooms.includes(room.room_id) && (
                <div className="border-t">
                  {room.items?.map((item) => {
                    const statusConfig = CHANGE_STATUS_CONFIG[item.change_status];
                    const StatusIcon = statusConfig?.icon || Minus;
                    
                    return (
                      <div key={item.item_name} className={`p-4 border-b last:border-b-0 ${item.change_status === 'deteriorated' ? 'bg-red-50' : ''}`}>
                        {/* Item Header */}
                        <div className="flex items-center justify-between mb-3">
                          <h4 className="font-medium">{item.item_name}</h4>
                          <Badge className={`${statusConfig?.bg} ${statusConfig?.color} border-0`}>
                            <StatusIcon className="w-3 h-3 mr-1" />
                            {statusConfig?.label}
                          </Badge>
                        </div>
                        
                        {/* Before / After Grid */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {/* Before (Check-In) */}
                          <div className="bg-blue-50 rounded-lg p-3">
                            <p className="text-xs font-medium text-blue-600 mb-2">CHECK-IN (Before)</p>
                            {item.checkin ? (
                              <>
                                <div className="flex items-center gap-4 mb-2">
                                  <div className="flex items-center gap-1">
                                    <span className={`w-3 h-3 rounded-full ${CONDITION_COLORS[item.checkin.condition] || 'bg-slate-400'}`}></span>
                                    <span className="text-sm capitalize">{item.checkin.condition?.replace('_', ' ')}</span>
                                  </div>
                                  <div className="flex items-center gap-1">
                                    <span className={`w-3 h-3 rounded-full ${CONDITION_COLORS[item.checkin.cleanliness] || 'bg-slate-400'}`}></span>
                                    <span className="text-sm capitalize">{item.checkin.cleanliness?.replace('_', ' ')}</span>
                                  </div>
                                </div>
                                {item.checkin.notes && (
                                  <p className="text-sm text-muted-foreground bg-white p-2 rounded">{item.checkin.notes}</p>
                                )}
                                {item.checkin.photos?.length > 0 && (
                                  <div className="flex flex-wrap gap-1 mt-2">
                                    {item.checkin.photos.slice(0, 3).map((photo) => (
                                      <img 
                                        key={photo.photo_id}
                                        src={photo.photo_data}
                                        alt=""
                                        className="w-12 h-12 object-cover rounded cursor-pointer"
                                        onClick={() => setShowPhotos({ photos: item.checkin.photos, title: `${item.item_name} - Check-In` })}
                                      />
                                    ))}
                                    {item.checkin.photos.length > 3 && (
                                      <div className="w-12 h-12 bg-slate-200 rounded flex items-center justify-center text-xs">
                                        +{item.checkin.photos.length - 3}
                                      </div>
                                    )}
                                  </div>
                                )}
                              </>
                            ) : (
                              <p className="text-sm text-muted-foreground italic">No data from check-in</p>
                            )}
                          </div>

                          {/* Arrow */}
                          <div className="hidden md:flex items-center justify-center absolute left-1/2 -translate-x-1/2">
                            <ArrowRight className="w-6 h-6 text-slate-400" />
                          </div>

                          {/* After (Check-Out) */}
                          <div className="bg-purple-50 rounded-lg p-3">
                            <p className="text-xs font-medium text-purple-600 mb-2">CHECK-OUT (After)</p>
                            <div className="flex items-center gap-4 mb-2">
                              <div className="flex items-center gap-1">
                                <span className={`w-3 h-3 rounded-full ${CONDITION_COLORS[item.checkout.condition] || 'bg-slate-400'}`}></span>
                                <span className="text-sm capitalize">{item.checkout.condition?.replace('_', ' ')}</span>
                              </div>
                              <div className="flex items-center gap-1">
                                <span className={`w-3 h-3 rounded-full ${CONDITION_COLORS[item.checkout.cleanliness] || 'bg-slate-400'}`}></span>
                                <span className="text-sm capitalize">{item.checkout.cleanliness?.replace('_', ' ')}</span>
                              </div>
                            </div>
                            {item.checkout.notes && (
                              <p className="text-sm text-muted-foreground bg-white p-2 rounded">{item.checkout.notes}</p>
                            )}
                            {item.checkout.photos?.length > 0 && (
                              <div className="flex flex-wrap gap-1 mt-2">
                                {item.checkout.photos.slice(0, 3).map((photo) => (
                                  <img 
                                    key={photo.photo_id}
                                    src={photo.photo_data}
                                    alt=""
                                    className="w-12 h-12 object-cover rounded cursor-pointer"
                                    onClick={() => setShowPhotos({ photos: item.checkout.photos, title: `${item.item_name} - Check-Out` })}
                                  />
                                ))}
                                {item.checkout.photos.length > 3 && (
                                  <div className="w-12 h-12 bg-slate-200 rounded flex items-center justify-center text-xs">
                                    +{item.checkout.photos.length - 3}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </Card>
          ))}
        </div>
      </main>

      {/* Photo Lightbox */}
      {showPhotos && (
        <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4" onClick={() => setShowPhotos(null)}>
          <div className="max-w-4xl w-full" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-white font-medium">{showPhotos.title}</h3>
              <Button variant="ghost" className="text-white" onClick={() => setShowPhotos(null)}>×</Button>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {showPhotos.photos.map((photo) => (
                <img 
                  key={photo.photo_id}
                  src={photo.photo_data}
                  alt=""
                  className="w-full h-48 object-cover rounded-lg"
                />
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
